import React from 'react'
import { useSEO } from '../hooks/useSEO'
import { SITE_INFO } from '../data/siteData'
import { ContactForm } from '../components/ContactForm'
import { useVirksomhedsoplysninger, useCMSSide } from '../hooks/useCMS'

export function KontaktPage() {
  const { info } = useVirksomhedsoplysninger()
  const { side: cmsSide } = useCMSSide('kontakt')
  const currentInfo = info || SITE_INFO

  useSEO({
    title: cmsSide?.seo_titel || 'KONTAKT | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Kontakt BoSteendahl.dk i Fredericia. Vendersgade 37 kl., 7000 Fredericia. Tlf: 53 12 82 13. Mail: bs@bosteendahl.dk. Hør mere om et forløb.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20220705-142422.jpg',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Kontakt Bo Steendahl
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Kontakt & Samtale'}
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-serif text-[#1b3a4b]/90 max-w-2xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Tag første skridt i dag. Hør mere om, hvordan et individuelt forløb kan skabe varig forandring for dig.'}
          </p>
        </div>
      </section>

      {/* Main Grid: Info + Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left: Contact Info & Hours (Span 5) */}
            <div className="lg:col-span-5 space-y-8">
              {/* Card with details */}
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
                <div>
                  <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block mb-1">
                    Praktiske oplysninger
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                    {currentInfo.name}
                  </h2>
                </div>

                <div className="space-y-5 text-sm sm:text-base text-[#475569]">
                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#1b3a4b] block font-semibold">Adresse</strong>
                      <span>{currentInfo.address}</span>
                      <br />
                      <span>{currentInfo.city}</span>
                      <div className="mt-1">
                        <a
                          href={currentInfo.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-[#7c875a] hover:underline inline-flex items-center gap-1"
                        >
                          <span>Vis på Google Maps</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#1b3a4b] block font-semibold">Telefon</strong>
                      <a
                        href={`tel:${currentInfo.phoneRaw}`}
                        className="text-[#1b3a4b] hover:text-[#7c875a] font-medium whitespace-nowrap"
                      >
                        {currentInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#1b3a4b] block font-semibold">Email</strong>
                      <a
                        href={`mailto:${currentInfo.email}`}
                        className="text-[#1b3a4b] hover:text-[#7c875a] font-medium whitespace-nowrap"
                      >
                        {currentInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-4 pt-2 border-t border-[#F1EBE4]">
                    <div className="w-10 h-10 rounded-xl bg-[#FAF8F5] text-[#64748B] flex items-center justify-center flex-shrink-0">
                      <i className="fa-solid fa-id-card text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="text-[#1b3a4b] block font-semibold">CVR-nummer</strong>
                      <span className="text-[#64748B]">{currentInfo.cvr}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Åbningstider Card */}
              <div className="bg-[#172e3b] text-white rounded-3xl p-8 sm:p-10 shadow-sm border border-white/10 space-y-4">
                <h3 className="font-serif text-2xl font-bold text-white flex items-center gap-2">
                  <i className="fa-regular fa-clock text-[#7c875a]" aria-hidden="true" />
                  <span>Åbningstider</span>
                </h3>
                <div className="space-y-2.5 text-sm divide-y divide-white/10 pt-2">
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[#FAF8F5]/80">Mandag</span>
                    <span className="font-medium text-white">08.00 - 16.00</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[#FAF8F5]/80">Tirsdag</span>
                    <span className="font-medium text-white">08.00 - 17.00</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[#FAF8F5]/80">Onsdag</span>
                    <span className="font-medium text-white">10.00 - 18.00</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[#FAF8F5]/80">Torsdag</span>
                    <span className="font-medium text-white">10.00 - 18.00</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[#FAF8F5]/80">Fredag</span>
                    <span className="font-medium text-white">08.00 - 14.00</span>
                  </div>
                </div>
              </div>

              {/* Beliggenhed / Kort */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-[#E5E0D8] space-y-4">
                <h4 className="font-serif text-lg font-bold text-[#1b3a4b]">
                  Find vej til klinikken
                </h4>
                <div className="rounded-2xl overflow-hidden border border-[#E5E0D8]">
                  <img
                    src="/_assets/kortvendersgade37.png"
                    alt="Kort over Vendersgade 37, 7000 Fredericia"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Right: Contact Form (Span 7) */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
